import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse } from '@mui/material';
import { GridRowOrderChangeParams, GridRowParams, useGridApiRef } from '@mui/x-data-grid-pro';
import { observer } from 'mobx-react-lite';
import { TMainVisualFormData } from 'src/@types';
import CDataGridPro from 'src/components/CDataGridPro';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import ServiceGuide, { ServicePreview } from 'src/components/guide/ServiceGuide';
import Iconify from 'src/components/iconify';
import { Page } from 'src/components/Page';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import useGetGridCol, { IMainVisualGridRow } from 'src/hooks/useGetGridCol';
import useGetGuideInfo from 'src/hooks/useGetGuideInfo';
import {
  ContentBox,
  ContentBoxButtons,
  ContentButtonCreate,
  ContentButtonText,
} from 'src/layouts/form/FormButton';
import { useStores } from 'src/models';
import { mainVisualApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FORM_IDS, FormType } from 'src/static/constants';

import { MainVisualFormDialog } from './MainVisualFormDailog';

/**
 * ## MainVisual
 * 메인 화면 관리 페이지
 */

export const MainVisual = () => {
  const { alertStore } = useStores();
  const { getMainVisualGridCol } = useGetGridCol();
  const { callApiWithLoading } = useCallApiWithLoading();
  const { openDialog, closeDialog } = useDialogContext();
  const { getMainVisualAreas } = useGetGuideInfo();
  const { t } = useTranslation();
  const handleResponse = useHandleResponse();
  const gridApiRef = useGridApiRef();
  // 그리드 row 데이터
  const [gridRows, setGridRows] = useState<IMainVisualGridRow[]>([]);
  // 가이드 토글
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  // 메인 비주얼 목록 조회
  const getMainVisualData = useCallback(async () => {
    await mainVisualApi.getMainvisual().then((res) => {
      if (res) {
        const newGridRows = res.data.map((mainVisual, i) => {
          return { ...mainVisual, id: mainVisual.visualSid ?? i };
        });
        setGridRows(newGridRows);
      }
    });
  }, []);

  useEffect(() => {
    getMainVisualData();
  }, [getMainVisualData]);

  // 등록 버튼: 등록 dialog open
  const handleAdd = () => {
    openDialog({
      id: FORM_IDS.MAINVISUAL,
      formType: FormType.CREATE,
      maxWidth: 'xl',
      dialogContent: (
        <MainVisualFormDialog formType={FormType.CREATE} onSubmitSuccess={handleSubmitSuccess} />
      ),
    });
  };

  // 그리드 더블 클릭 시: 수정 dialog open
  const handleRowDoubleClick = (param: GridRowParams<IMainVisualGridRow>) => {
    callApiWithLoading(async () => {
      await mainVisualApi.getDetail(param.row.visualSid).then((res) => {
        if (res) {
          openDialog({
            id: FORM_IDS.MAINVISUAL,
            formType: FormType.UPDATE,
            maxWidth: 'xl',
            dialogContent: (
              <MainVisualFormDialog
                formType={FormType.UPDATE}
                selectedData={res.data}
                onSubmitSuccess={handleSubmitSuccess}
              />
            ),
          });
        }
      });
    });
  };

  // 등록 & 수정 성공 시
  const handleSubmitSuccess = () => {
    closeDialog(FORM_IDS.MAINVISUAL);
    getMainVisualData();
  };

  // 가이드 토글
  const handleGuideToggle = () => {
    setIsGuideOpen(!isGuideOpen);
  };

  const handleRowOrderChange = async (param: GridRowOrderChangeParams) => {
    // 현재 데이터의 스냅샷 저장
    const originalRows = structuredClone(gridRows); // 깊은 복사로 원본 데이터 저장
    try {
      // 드래그 시작할 때 알림
      alertStore.setProps({
        open: true,
        title: '순서 변경 안내',
        content: '드래그하여 순서를 변경하면 즉시 반영됩니다.',
        category: 'info',
        hasCancelButton: true,
      });
      alertStore.setOnCancel(() => {
        setGridRows(originalRows); // 취소 시 원래 데이터로 복원
      });
      // 순서 변경 버튼 클릭 시 실행될 콜백
      alertStore.setOnConfirm(async () => {
        const targetRow = {
          ...param.row,
          ordr: param.targetIndex + 1,
        } as TMainVisualFormData;

        const result = await mainVisualApi.put(param.row.visualSid, targetRow);

        if (result) {
          handleResponse(
            '순서 변경이 완료되었습니다.',
            '순서 변경이 실패되었습니다.',
            getMainVisualData,
          );
        }
      });
    } catch (error) {
      console.log('순서 변경 실패:', error);
      setGridRows(originalRows); // 실패 시 원래 데이터로 복원
    }
  };
  return (
    <Page>
      <ContentBox>
        <ContentBoxButtons>
          <ContentButtonCreate onClick={handleAdd}>
            {t('common.button.register')}
          </ContentButtonCreate>
          <ContentButtonText onClick={handleGuideToggle}>
            <Iconify icon="tdesign:info-circle" />
          </ContentButtonText>
        </ContentBoxButtons>
        <Collapse in={isGuideOpen}>
          <ServicePreview>
            <ServiceGuide areas={getMainVisualAreas()} />
          </ServicePreview>
        </Collapse>
        <CDataGridPro
          apiRef={gridApiRef}
          columns={getMainVisualGridCol()}
          rows={gridRows}
          rowCount={gridRows.length}
          rowReordering={true}
          rowHeight={150}
          onRowDoubleClick={handleRowDoubleClick}
          onRowOrderChange={handleRowOrderChange}
          slots={{ footer: () => null }}
        />
      </ContentBox>
    </Page>
  );
};

export default observer(MainVisual);
